import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import { Button } from './ui/Button';
import { Label } from './ui/Label';
import { 
  MessageSquare, 
  Play, 
  CheckCircle,
  Clock,
  RefreshCw,
  Eye,
  QrCode,
  Power,
  LogOut,
  Activity
} from 'lucide-react';
import { apiService } from '../services/api';

const DEFAULT_RENEWAL_TEMPLATE = `✅ *Renovação Realizada com Sucesso!*

Olá!

Sua conta foi renovada com sucesso.

👤 *Usuário:*
{username}

🔑 *Senha:*
{password}

📅 *Nova data de vencimento:* {expDate}
📦 *Pacote:* {packageName}
🔗 *Conexões:* {maxConnections}
🖥️ *Sistema:* {system}

Sua conta está ativa e funcionando normalmente!`;

interface WhatsappAutomationProps {
  isConnected?: boolean; // Opcional, o componente busca internamente
}

const WhatsappAutomation: React.FC<WhatsappAutomationProps> = ({ isConnected: initialIsConnected }) => {
  const [activeSubTab, setActiveSubTab] = useState<'automations' | 'history'>('automations');
  
  // Estados para conexão WhatsApp
  const [whatsappStatus, setWhatsappStatus] = useState<any>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(initialIsConnected);
  const [isStarting, setIsStarting] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const qrCodeIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Estados para Template padrão de aviso de vencimento
  const [expirationWarningTemplate, setExpirationWarningTemplate] = useState<any>(null);
  const [editingExpirationTemplate, setEditingExpirationTemplate] = useState(false);
  const [expirationTemplateContent, setExpirationTemplateContent] = useState('');
  const [savingExpirationTemplate, setSavingExpirationTemplate] = useState(false);
  const [loadingExpirationTemplate, setLoadingExpirationTemplate] = useState(false);

  // Estados para Template padrão de renovação (copiar no painel e disparos)
  const [renewalTemplate, setRenewalTemplate] = useState<any>(null);
  const [editingRenewalTemplate, setEditingRenewalTemplate] = useState(false);
  const [renewalTemplateContent, setRenewalTemplateContent] = useState('');
  const [savingRenewalTemplate, setSavingRenewalTemplate] = useState(false);
  const [loadingRenewalTemplate, setLoadingRenewalTemplate] = useState(false);
  
  // Estados para Logs
  const [logs, setLogs] = useState<any[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [queueStatus, setQueueStatus] = useState<any>(null);
  
  // Estados para Disparo de Aviso de Vencimento (janela: 1 a 7 dias antes do vencimento)
  const [expirationWarningConfig, setExpirationWarningConfig] = useState<any>({
    enabled: false,
    templateId: null,
    hour: 8,
    daysBeforeExpiration: 7,
    lastRun: null,
    totalSent: 0
  });

  const clampExpirationDays = (value: unknown) => {
    const n = parseInt(String(value), 10);
    if (!Number.isFinite(n)) return 7;
    return Math.min(7, Math.max(1, n));
  };
  const [loadingExpirationWarningConfig, setLoadingExpirationWarningConfig] = useState(false);
  const [savingExpirationWarningConfig, setSavingExpirationWarningConfig] = useState(false);

  // Converter QR code base64 para data URL
  const formatQRCode = (qrCodeString: string | null | undefined): string | null => {
    if (!qrCodeString) return null;
    
    // Se já é uma data URL, retornar como está
    if (qrCodeString.startsWith('data:')) {
      return qrCodeString;
    }
    
    // Se é uma URL HTTP/HTTPS, retornar como está
    if (qrCodeString.startsWith('http://') || qrCodeString.startsWith('https://')) {
      return qrCodeString;
    }
    
    // Detectar se é uma string do Baileys (contém caracteres especiais como @, /, =, +)
    // Essas strings não são base64 válido e precisam ser convertidas no backend
    if ((qrCodeString.includes('@') || qrCodeString.includes('/') || qrCodeString.includes('=')) && qrCodeString.length > 100) {
      console.warn('QR code recebido parece ser string do Baileys (não é imagem). O backend deve converter isso.');
      return null; // Retornar null para forçar nova busca ou indicar que precisa ser convertido
    }
    
    // Se é base64 puro, converter para data URL
    // O backend agora deve retornar como data URL, mas mantemos fallback
    try {
      // Verificar se já tem o prefixo base64
      if (qrCodeString.includes(',')) {
        return qrCodeString; // Já está formatado
      }
      // Tentar validar se é base64 válido
      const base64Regex = /^[A-Za-z0-9+/=]+$/;
      if (base64Regex.test(qrCodeString) && qrCodeString.length > 50) {
        // Adicionar prefixo data URL
        return `data:image/png;base64,${qrCodeString}`;
      }
      // Se não passar na validação, retornar null
      console.warn('QR code não parece ser base64 válido:', qrCodeString.substring(0, 50) + '...');
      return null;
    } catch (error) {
      console.error('Erro ao formatar QR code:', error);
      return null;
    }
  };

  // Buscar status do WhatsApp (inclui QR code se não conectado)
  const fetchWhatsappStatus = async () => {
    try {
      setLoadingStatus(true);
      const response = await apiService.getWhatsappAutomationStatus();
      if (response.success && response.data) {
        setWhatsappStatus(response.data);
        const status = response.data.status || 'disconnected';
        setIsConnected(status === 'connected');
        
        // Verificar QR code do lastQRCode
        if (status !== 'connected' && response.data.lastQRCode) {
          const qrData = response.data.lastQRCode;
          // Verificar se o QR code ainda é válido
          if (qrData.code && qrData.expiresAt && new Date(qrData.expiresAt) > new Date()) {
            setQrCode(formatQRCode(qrData.code));
          } else {
            setQrCode(null);
          }
        } else {
          setQrCode(null);
        }
      }
    } catch (error: any) {
      console.error('Erro ao buscar status do WhatsApp:', error);
      setIsConnected(false);
      setQrCode(null);
    } finally {
      setLoadingStatus(false);
    }
  };

  // Iniciar sessão do WhatsApp
  const handleStartSession = async () => {
    try {
      setIsStarting(true);
      const response = await apiService.startWhatsappAutomation();
      
      if (response.success) {
        // Buscar status atualizado (que inclui o QR code formatado)
        await fetchWhatsappStatus();
        
        // Mesmo que a resposta do /start não traga QR ainda, o backend pode levar alguns segundos.
        // Iniciar polling sempre que ainda não estiver conectado.
        startQrCodePolling();
      } else {
        alert(response.message || 'Erro ao iniciar sessão do WhatsApp');
      }
    } catch (error: any) {
      alert(error?.response?.data?.error || 'Erro ao iniciar sessão do WhatsApp');
    } finally {
      setIsStarting(false);
    }
  };

  // Remover sessão do WhatsApp
  const handleLogout = async () => {
    if (!window.confirm('Tem certeza que deseja desconectar o WhatsApp? Isso interromperá o envio de mensagens automáticas.')) {
      return;
    }

    try {
      setIsLoggingOut(true);
      const response = await apiService.logoutWhatsappAutomation();
      
      if (response.success) {
        await fetchWhatsappStatus();
        stopQrCodePolling();
        alert('WhatsApp desconectado com sucesso!');
      } else {
        alert(response.message || 'Erro ao desconectar WhatsApp');
      }
    } catch (error: any) {
      alert(error?.response?.data?.error || 'Erro ao desconectar WhatsApp');
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Resetar sessão do WhatsApp
  const handleReset = async () => {
    if (!window.confirm('Tem certeza que deseja resetar a sessão do WhatsApp? Isso limpará todos os dados da sessão e será necessário escanear o QR Code novamente.')) {
      return;
    }

    try {
      setIsResetting(true);
      const response = await apiService.resetWhatsappAutomation();
      
      if (response.success) {
        await fetchWhatsappStatus();
        stopQrCodePolling();
        alert('Sessão resetada com sucesso!');
      } else {
        alert(response.message || 'Erro ao resetar sessão');
      }
    } catch (error: any) {
      alert(error?.response?.data?.error || 'Erro ao resetar sessão');
    } finally {
      setIsResetting(false);
    }
  };

  // Formatar data para exibição
  const formatDateTime = (date: Date | string | null | undefined) => {
    if (!date) return '--';
    try {
      const d = typeof date === 'string' ? new Date(date) : date;
      return d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return '--';
    }
  };

  // Polling do QR code quando não conectado
  const startQrCodePolling = () => {
    stopQrCodePolling(); // Limpar qualquer polling anterior
    
    qrCodeIntervalRef.current = setInterval(async () => {
      const response = await apiService.getWhatsappAutomationStatus();
      if (response.success && response.data) {
        const status = response.data.status || 'disconnected';
        setWhatsappStatus(response.data);
        
        if (status === 'connected') {
          setIsConnected(true);
          setQrCode(null);
          stopQrCodePolling();
        } else if (response.data.lastQRCode) {
          const qrData = response.data.lastQRCode;
          if (qrData.code && qrData.expiresAt && new Date(qrData.expiresAt) > new Date()) {
            setQrCode(formatQRCode(qrData.code));
          } else {
            setQrCode(null);
          }
        }
      }
    }, 2500); // Polling mais frequente enquanto aguarda QR (backend pode demorar >10s)
  };

  // Parar polling do QR code
  const stopQrCodePolling = () => {
    if (qrCodeIntervalRef.current) {
      clearInterval(qrCodeIntervalRef.current);
      qrCodeIntervalRef.current = null;
    }
  };

  // Buscar template padrão de aviso de vencimento
  const fetchExpirationWarningTemplate = async () => {
    try {
      setLoadingExpirationTemplate(true);
      const response = await apiService.getExpirationWarningTemplate();
      if (response.success && response.data) {
        setExpirationWarningTemplate(response.data);
        setExpirationTemplateContent(response.data.body || response.data.content || '');
      }
    } catch (error: any) {
      if (error?.response?.status === 401) {
        alert('Sessão expirada. Por favor, faça login novamente.');
      } else if (error?.response?.status === 403) {
        console.warn('Acesso negado ao buscar template padrão');
      }
    } finally {
      setLoadingExpirationTemplate(false);
    }
  };

  // Atualizar template padrão de aviso de vencimento
  const handleSaveExpirationWarningTemplate = async () => {
    if (!expirationTemplateContent.trim()) {
      alert('O conteúdo do template não pode estar vazio.');
      return;
    }

    try {
      setSavingExpirationTemplate(true);
      const response = await apiService.updateExpirationWarningTemplate(expirationTemplateContent);
      
      if (response.success) {
        alert('Template de aviso de vencimento atualizado com sucesso!');
        setEditingExpirationTemplate(false);
        await fetchExpirationWarningTemplate();
        
        // Atualizar configuração de aviso de vencimento com o novo template
        if (response.data && response.data._id) {
          setExpirationWarningConfig((prev: any) => ({
            ...prev,
            templateId: response.data._id
          }));
          
          // Se o disparo estiver ativado, salvar a configuração automaticamente
          if (expirationWarningConfig.enabled) {
            await handleUpdateExpirationWarningConfig();
          }
        }
      }
    } catch (error: any) {
      alert(error?.response?.data?.error || 'Erro ao salvar template.');
    } finally {
      setSavingExpirationTemplate(false);
    }
  };

  // Buscar template padrão de renovação
  const fetchRenewalTemplate = async () => {
    try {
      setLoadingRenewalTemplate(true);
      const response = await apiService.getRenewalTemplate();
      if (response.success && response.data) {
        setRenewalTemplate(response.data);
        setRenewalTemplateContent(response.data.body || response.data.content || '');
      }
    } catch (error: any) {
      if (error?.response?.status === 401) {
        alert('Sessão expirada. Por favor, faça login novamente.');
      } else if (error?.response?.status === 403) {
        console.warn('Acesso negado ao buscar template de renovação');
      }
    } finally {
      setLoadingRenewalTemplate(false);
    }
  };

  // Atualizar ou criar template padrão de renovação
  const handleSaveRenewalTemplate = async () => {
    const toSave = (renewalTemplate ? renewalTemplateContent : (renewalTemplateContent || DEFAULT_RENEWAL_TEMPLATE)).trim();
    if (!toSave) {
      alert('O conteúdo do template não pode estar vazio.');
      return;
    }
    try {
      setSavingRenewalTemplate(true);
      const response = await apiService.updateRenewalTemplate(toSave);
      if (response.success) {
        alert(renewalTemplate ? 'Template de renovação atualizado com sucesso!' : 'Template de renovação criado com sucesso!');
        setEditingRenewalTemplate(false);
        await fetchRenewalTemplate();
      }
    } catch (error: any) {
      alert(error?.response?.data?.error || 'Erro ao salvar template.');
    } finally {
      setSavingRenewalTemplate(false);
    }
  };

  // Buscar logs e fila
  const fetchLogsAndQueue = async () => {
    try {
      setLoadingLogs(true);
      const [logsRes, queueRes] = await Promise.all([
        apiService.getWhatsappAutomationLogs({ limit: 50 }),
        apiService.getWhatsappQueueStatus()
      ]);
      setLogs(logsRes.data || []);
      setQueueStatus(queueRes.data);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        alert('Sessão expirada. Por favor, faça login novamente.');
      } else if (error?.response?.status === 403) {
        console.warn('Acesso negado ao buscar logs e fila do WhatsApp');
      }
      setLogs([]);
      setQueueStatus(null);
    } finally {
      setLoadingLogs(false);
    }
  };

  // Buscar configuração de aviso de vencimento
  const fetchExpirationWarningConfig = async () => {
    try {
      setLoadingExpirationWarningConfig(true);
      const response = await apiService.getWhatsappDailyDispatchConfig();
      if (response.success && response.data) {
        setExpirationWarningConfig({
          ...response.data,
          daysBeforeExpiration: clampExpirationDays(response.data.daysBeforeExpiration)
        });
        
        // Se houver template padrão, usar automaticamente
        if (!response.data.templateId && expirationWarningTemplate) {
          setExpirationWarningConfig((prev: any) => ({
            ...prev,
            templateId: expirationWarningTemplate._id
          }));
        }
      }
    } catch (error: any) {
      console.error('Erro ao buscar configuração de aviso de vencimento:', error);
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message;
      if (error?.response?.status === 401) {
        alert('Sessão expirada. Faça login novamente.');
      } else if (msg) {
        alert(msg);
      }
    } finally {
      setLoadingExpirationWarningConfig(false);
    }
  };

  // Atualizar configuração de aviso de vencimento
  const handleUpdateExpirationWarningConfig = async () => {
    const resolvedTemplateId =
      expirationWarningConfig.templateId || expirationWarningTemplate?._id;
    if (expirationWarningConfig.enabled && !resolvedTemplateId) {
      alert('É necessário ter o template padrão de aviso de vencimento configurado.');
      return;
    }

    try {
      setSavingExpirationWarningConfig(true);
      const config = {
        enabled: expirationWarningConfig.enabled,
        templateId: resolvedTemplateId,
        hour: expirationWarningConfig.hour,
        daysBeforeExpiration: clampExpirationDays(expirationWarningConfig.daysBeforeExpiration)
      };
      
      const response = await apiService.updateWhatsappDailyDispatchConfig(config);

      if (response.success) {
        alert(response.message || 'Configuração de aviso de vencimento salva com sucesso!');
        await fetchExpirationWarningConfig();
      } else {
        alert(response.error || response.message || 'Não foi possível salvar a configuração.');
      }
    } catch (error: any) {
      const errBody = error?.response?.data;
      alert(
        errBody?.error ||
          errBody?.message ||
          error?.message ||
          'Erro ao salvar configuração.'
      );
    } finally {
      setSavingExpirationWarningConfig(false);
    }
  };

  // Testar disparo de aviso de vencimento
  const handleTestExpirationWarningDispatch = async () => {
    if (!window.confirm('Deseja executar um teste do disparo de aviso de vencimento agora?\n\nAs mensagens serão adicionadas à fila normalmente.')) {
      return;
    }

    try {
      const response = await apiService.testWhatsappDailyDispatch();
      
      if (response.success) {
        alert(response.message || 'Teste executado! Verifique a fila de mensagens.');
        // Atualizar histórico após teste
        await fetchLogsAndQueue();
      }
    } catch (error: any) {
      alert(error?.response?.data?.error || 'Erro ao executar teste.');
    }
  };

  // Buscar status do WhatsApp ao montar componente
  useEffect(() => {
    fetchWhatsappStatus();
    
    return () => {
      stopQrCodePolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Atualizar quando status mudar
  useEffect(() => {
    const status = whatsappStatus?.status || 'disconnected';
    // Enquanto estiver desconectado e em fase de pareamento, manter polling ativo
    if (!isConnected && (status === 'initializing' || status === 'awaiting_qr')) {
      startQrCodePolling();
    } else if (isConnected) {
      stopQrCodePolling();
    }
    
    return () => {
      stopQrCodePolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, whatsappStatus?.status]);

  useEffect(() => {
    if (activeSubTab === 'automations') {
      fetchExpirationWarningTemplate();
      fetchRenewalTemplate();
      fetchExpirationWarningConfig();
    } else if (activeSubTab === 'history' && isConnected) {
      fetchLogsAndQueue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubTab, isConnected]);

  // Atualizar templateId quando o template padrão for carregado
  useEffect(() => {
    if (expirationWarningTemplate && expirationWarningConfig && !expirationWarningConfig.templateId) {
      setExpirationWarningConfig((prev: any) => ({
        ...prev,
        templateId: expirationWarningTemplate._id
      }));
    }
  }, [expirationWarningTemplate, expirationWarningConfig]);

  return (
    <div className="space-y-6">
      {/* Card de Status da Sessão */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Status da Sessão
          </CardTitle>
          <CardDescription>
            Gerencie a conexão do seu número com o WhatsApp Web.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingStatus ? (
            <div className="text-center py-4">
              <RefreshCw className="h-6 w-6 animate-spin mx-auto" />
              <p className="text-sm text-muted-foreground mt-2">Carregando status...</p>
            </div>
          ) : (
            <>
              {/* Status Visual */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground mb-2">Status da Conexão</p>
                  <button
                    className={`w-full py-3 px-4 rounded-lg font-medium text-lg transition-colors ${
                      isConnected
                        ? 'bg-green-100 text-green-800 border-2 border-green-300'
                        : 'bg-gray-100 text-gray-600 border-2 border-gray-300'
                    }`}
                    disabled
                  >
                    {isConnected ? '✅ Conectado' : '❌ Desconectado'}
                  </button>
                  {!isConnected && (
                    <p className="text-xs text-muted-foreground mt-2 text-center">
                      Inicie a sessão para começar a enviar mensagens.
                    </p>
                  )}
                </div>
              </div>

              {/* Informações da Sessão */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">ÚLTIMA CONDIÇÃO</p>
                  <p className="text-sm font-mono">
                    {formatDateTime(whatsappStatus?.lastConnectedAt)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">ÚLTIMA DESCONEXÃO</p>
                  <p className="text-sm font-mono">
                    {formatDateTime(whatsappStatus?.lastDisconnectedAt)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">MOTIVO DO ÚLTIMO ERRO</p>
                  <p className="text-sm font-mono text-red-600">
                    {whatsappStatus?.lastError || whatsappStatus?.lastDisconnectReason || '--'}
                  </p>
                </div>
              </div>

              {/* Botões de Ação */}
              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={fetchWhatsappStatus}
                  disabled={loadingStatus}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loadingStatus ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
                {!isConnected ? (
                  <Button
                    onClick={handleStartSession}
                    disabled={isStarting || loadingStatus}
                  >
                    <Power className="h-4 w-4 mr-2" />
                    {isStarting ? 'Iniciando...' : 'Iniciar Sessão'}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={handleLogout}
                    disabled={isLoggingOut}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    {isLoggingOut ? 'Desconectando...' : 'Remover Sessão'}
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={handleReset}
                  disabled={isResetting || loadingStatus}
                  className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${isResetting ? 'animate-spin' : ''}`} />
                  {isResetting ? 'Resetando...' : 'Resetar Sessão'}
                </Button>
              </div>

              {/* QR Code quando não conectado */}
              {!isConnected && (
                <div className="pt-4 border-t">
                  {qrCode ? (
                    <div className="flex flex-col items-center space-y-4 p-4 border rounded-lg bg-white">
                      <div className="text-center">
                        <QrCode className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm font-medium mb-2">Escaneie o QR Code com o WhatsApp</p>
                        <p className="text-xs text-muted-foreground mb-4">
                          Abra o WhatsApp no seu celular → Menu → Dispositivos conectados → Conectar um dispositivo
                        </p>
                      </div>
                      <div className="p-4 bg-white border-2 border-gray-300 rounded-lg">
                        {qrCode ? (
                          <img 
                            src={qrCode} 
                            alt="QR Code WhatsApp" 
                            className="w-64 h-64 mx-auto"
                            onError={(e) => {
                              console.error('Erro ao carregar QR code:', qrCode);
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="w-64 h-64 mx-auto flex items-center justify-center text-gray-400">
                            <QrCode className="h-16 w-16" />
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground text-center">
                        O QR code atualiza automaticamente a cada 5 segundos
                      </p>
                    </div>
                  ) : (
                    <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center bg-gray-50">
                      <QrCode className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm text-muted-foreground">
                        Clique em "Iniciar Sessão" para gerar o QR Code
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Sub-abas */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveSubTab('automations')}
          className={`px-4 py-2 font-medium transition-colors border-b-2 ${
            activeSubTab === 'automations'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <MessageSquare className="h-4 w-4 inline mr-2" />
          Mensagens Automáticas
        </button>
        <button
          onClick={() => isConnected && setActiveSubTab('history')}
          disabled={!isConnected}
          className={`px-4 py-2 font-medium transition-colors border-b-2 ${
            activeSubTab === 'history'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          } ${!isConnected ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <Clock className="h-4 w-4 inline mr-2" />
          Histórico
        </button>
      </div>

      {/* Aba: Mensagens Automáticas */}
      {activeSubTab === 'automations' && (
        <div className="space-y-4">
          {/* Card de Aviso de Vencimento */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                ⏰ Aviso de Vencimento (automático)
              </CardTitle>
              <CardDescription>
                Envie avisos automaticamente para clientes com linha ativa cuja data de vencimento esteja dentro da janela escolhida (1 a 7 dias).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingExpirationWarningConfig ? (
                <div className="text-center py-4 text-muted-foreground">
                  Carregando configuração...
                </div>
              ) : (
                <>
                  {/* Switch para ativar/desativar */}
                  <div className="flex items-center justify-between p-4 border rounded-lg bg-blue-50">
                    <div>
                      <div className="font-medium text-blue-900">
                        {expirationWarningConfig.enabled ? '✅ Aviso de Vencimento Ativado' : '⏸️ Aviso de Vencimento Desativado'}
                      </div>
                      <p className="text-sm text-blue-700 mt-1">
                        {expirationWarningConfig.enabled 
                          ? `Mensagens serão enviadas automaticamente às ${expirationWarningConfig.hour}:00h para clientes com vencimento em até ${clampExpirationDays(expirationWarningConfig.daysBeforeExpiration)} dia(s). O texto da mensagem usa os dias restantes reais de cada cliente.`
                          : 'Ative para começar a enviar avisos de vencimento automaticamente.'}
                      </p>
                      {!isConnected && (
                        <p className="text-xs text-amber-800 mt-2">
                          O envio automático só ocorre com o WhatsApp conectado; você pode salvar a configuração antes de conectar.
                        </p>
                      )}
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={expirationWarningConfig.enabled}
                        onChange={(e) => setExpirationWarningConfig({ ...expirationWarningConfig, enabled: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
                    </label>
                  </div>

                  {/* Configurações */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Horário do Envio</Label>
                      <select
                        className="w-full border rounded-md px-3 py-2 mt-1"
                        value={expirationWarningConfig.hour}
                        onChange={(e) => setExpirationWarningConfig({ ...expirationWarningConfig, hour: parseInt(e.target.value) })}
                      >
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i}>
                            {i.toString().padStart(2, '0')}:00h
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <Label>Início do disparo (dias antes do vencimento)</Label>
                      <select
                        className="w-full border rounded-md px-3 py-2 mt-1"
                        value={clampExpirationDays(expirationWarningConfig.daysBeforeExpiration)}
                        onChange={(e) =>
                          setExpirationWarningConfig({
                            ...expirationWarningConfig,
                            daysBeforeExpiration: parseInt(e.target.value, 10)
                          })
                        }
                      >
                        {[7, 6, 5, 4, 3, 2, 1].map((d) => (
                          <option key={d} value={d}>
                            Até {d} dia{d !== 1 ? 's' : ''} antes
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-muted-foreground mt-1">
                        Só entram clientes com vencimento dentro desta janela. Valores de 1 a 7.
                      </p>
                    </div>
                  </div>

                  {/* Botões */}
                  <div className="flex gap-2">
                    <Button
                      onClick={handleUpdateExpirationWarningConfig}
                      disabled={savingExpirationWarningConfig}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      {savingExpirationWarningConfig ? 'Salvando...' : 'Salvar Configuração'}
                    </Button>
                    
                    {expirationWarningConfig.enabled && expirationWarningConfig.templateId && isConnected && (
                      <Button
                        variant="outline"
                        onClick={handleTestExpirationWarningDispatch}
                      >
                        <Play className="h-4 w-4 mr-2" />
                        Testar Agora
                      </Button>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Card de Template Padrão de Aviso de Vencimento (Editável) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Template de Aviso de Vencimento
              </CardTitle>
              <CardDescription>
                Edite o template padrão que será usado para enviar avisos de vencimento.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingExpirationTemplate ? (
                <div className="text-center py-4 text-muted-foreground">
                  Carregando template...
                </div>
              ) : expirationWarningTemplate ? (
                <>
                  {!editingExpirationTemplate ? (
                    <>
                      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900">{expirationWarningTemplate.name}</h4>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingExpirationTemplate(true);
                              setExpirationTemplateContent(expirationWarningTemplate.body || expirationWarningTemplate.content || '');
                            }}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Editar Template
                          </Button>
                        </div>
                        <div className="text-sm text-gray-700 bg-white p-4 rounded border font-mono whitespace-pre-wrap max-h-60 overflow-y-auto">
                          {expirationWarningTemplate.body || expirationWarningTemplate.content}
                        </div>
                      </div>
                      
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                        <p className="font-medium text-blue-900 mb-2">📝 Variáveis Disponíveis</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                          <code className="px-2 py-1 bg-white rounded border border-blue-200">{'{username}'}</code>
                          <code className="px-2 py-1 bg-white rounded border border-blue-200">{'{password}'}</code>
                          <code className="px-2 py-1 bg-white rounded border border-blue-200">{'{email}'}</code>
                          <code className="px-2 py-1 bg-white rounded border border-blue-200">{'{whatsapp}'}</code>
                          <code className="px-2 py-1 bg-white rounded border border-blue-200">{'{expDate}'}</code>
                          <code className="px-2 py-1 bg-white rounded border border-blue-200">{'{daysToExpire}'}</code>
                          <code className="px-2 py-1 bg-white rounded border border-blue-200">{'{maxConnections}'}</code>
                          <code className="px-2 py-1 bg-white rounded border border-blue-200">{'{packageName}'}</code>
                          <code className="px-2 py-1 bg-white rounded border border-blue-200">{'{system}'}</code>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="p-4 border border-primary/20 bg-primary/5 rounded-lg space-y-4">
                        <div>
                          <Label>Conteúdo do Template *</Label>
                          <textarea
                            className="w-full border rounded-md px-3 py-2 min-h-[200px] font-mono text-sm mt-1"
                            value={expirationTemplateContent}
                            onChange={(e) => setExpirationTemplateContent(e.target.value)}
                            placeholder={`⚠️ *ATENÇÃO - Aviso de Vencimento* ⚠️

Olá!

👤 *Seu usuário:*
{username}

🔑 *Sua senha:*
{password}

Seu acesso expira em *{daysToExpire} dias*.

📅 *Data de vencimento:* {expDate}
📦 *Pacote:* {packageName}
🔗 *Conexões:* {maxConnections}

Renove agora para não perder o acesso!`}
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            💡 Variáveis: {'{username}'}, {'{password}'}, {'{email}'}, {'{whatsapp}'}, {'{expDate}'}, {'{daysToExpire}'}, {'{maxConnections}'}, {'{packageName}'}, {'{system}'}
                          </p>
                        </div>

                        <div className="flex gap-2">
                          <Button
                            onClick={handleSaveExpirationWarningTemplate}
                            disabled={savingExpirationTemplate}
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            {savingExpirationTemplate ? 'Salvando...' : 'Salvar Template'}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              setEditingExpirationTemplate(false);
                              setExpirationTemplateContent(expirationWarningTemplate.body || expirationWarningTemplate.content || '');
                            }}
                          >
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                  <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p className="font-medium">Template padrão não encontrado</p>
                  <p className="text-sm mt-2">Tente recarregar a página.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Card de Template Padrão de Renovação (copiar no painel e disparos) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5" />
                Template de Renovação
              </CardTitle>
              <CardDescription>
                Template usado ao copiar mensagem no painel após renovar e nos disparos de renovação. O aviso de vencimento (WhatsApp) usa o template próprio.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingRenewalTemplate ? (
                <div className="text-center py-4 text-muted-foreground">
                  Carregando template...
                </div>
              ) : renewalTemplate ? (
                <>
                  {!editingRenewalTemplate ? (
                    <>
                      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900">{renewalTemplate.name}</h4>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingRenewalTemplate(true);
                              setRenewalTemplateContent(renewalTemplate.body || renewalTemplate.content || '');
                            }}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Editar Template
                          </Button>
                        </div>
                        <div className="text-sm text-gray-700 bg-white p-4 rounded border font-mono whitespace-pre-wrap max-h-60 overflow-y-auto">
                          {renewalTemplate.body || renewalTemplate.content}
                        </div>
                      </div>
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                        <p className="font-medium text-green-900 mb-2">📝 Variáveis: {'{username}'}, {'{password}'}, {'{expDate}'}, {'{packageName}'}, {'{maxConnections}'}, {'{system}'}, {'{email}'}, {'{whatsapp}'}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="p-4 border border-primary/20 bg-primary/5 rounded-lg space-y-4">
                        <div>
                          <Label>Conteúdo do Template *</Label>
                          <textarea
                            className="w-full border rounded-md px-3 py-2 min-h-[200px] font-mono text-sm mt-1"
                            value={renewalTemplateContent}
                            onChange={(e) => setRenewalTemplateContent(e.target.value)}
                            placeholder={DEFAULT_RENEWAL_TEMPLATE}
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            💡 Use: {'{username}'}, {'{password}'}, {'{expDate}'}, {'{packageName}'}, {'{maxConnections}'}, {'{system}'}, {'{email}'}, {'{whatsapp}'}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={handleSaveRenewalTemplate}
                            disabled={savingRenewalTemplate}
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            {savingRenewalTemplate ? 'Salvando...' : 'Salvar Template'}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              setEditingRenewalTemplate(false);
                              setRenewalTemplateContent(renewalTemplate.body || renewalTemplate.content || '');
                            }}
                          >
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <>
                  <div className="p-4 border border-primary/20 bg-primary/5 rounded-lg space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Template ainda não configurado ou não foi possível carregá-lo. Preencha abaixo e salve para criar. Será usado ao copiar mensagem no painel após renovar e nos disparos de renovação.
                    </p>
                    <Button variant="outline" size="sm" onClick={fetchRenewalTemplate} disabled={loadingRenewalTemplate} className="mb-2">
                      <RefreshCw className={`h-4 w-4 mr-2 ${loadingRenewalTemplate ? 'animate-spin' : ''}`} />
                      Tentar carregar novamente
                    </Button>
                    <div>
                      <Label>Conteúdo do Template *</Label>
                      <textarea
                        className="w-full border rounded-md px-3 py-2 min-h-[200px] font-mono text-sm mt-1"
                        value={renewalTemplateContent || DEFAULT_RENEWAL_TEMPLATE}
                        onChange={(e) => setRenewalTemplateContent(e.target.value)}
                        placeholder={DEFAULT_RENEWAL_TEMPLATE}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        💡 Use: {'{username}'}, {'{password}'}, {'{expDate}'}, {'{packageName}'}, {'{maxConnections}'}, {'{system}'}, {'{email}'}, {'{whatsapp}'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleSaveRenewalTemplate}
                        disabled={savingRenewalTemplate || !(renewalTemplateContent || DEFAULT_RENEWAL_TEMPLATE).trim()}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        {savingRenewalTemplate ? 'Salvando...' : 'Criar Template de Renovação'}
                      </Button>
                    </div>
                  </div>
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                    <p className="font-medium text-green-900 mb-2">📝 Variáveis: {'{username}'}, {'{password}'}, {'{expDate}'}, {'{packageName}'}, {'{maxConnections}'}, {'{system}'}, {'{email}'}, {'{whatsapp}'}</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Aba: Histórico */}
      {activeSubTab === 'history' && (
        <div className="space-y-4">
          {/* Status da Fila */}
          {queueStatus && (
            <Card>
              <CardHeader>
                <CardTitle>📊 Status da Fila</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4">
                  <div className="p-3 border rounded-lg">
                    <p className="text-sm text-muted-foreground">Na Fila</p>
                    <p className="text-2xl font-bold">{queueStatus.queueSize || 0}</p>
                  </div>
                  <div className="p-3 border rounded-lg">
                    <p className="text-sm text-muted-foreground">Última Hora</p>
                    <p className="text-2xl font-bold">{queueStatus.stats?.sentLastHour || 0}</p>
                    <p className="text-xs text-muted-foreground">
                      de {queueStatus.limits?.messagesPerHour || 50} permitidas
                    </p>
                  </div>
                  <div className="p-3 border rounded-lg">
                    <p className="text-sm text-muted-foreground">Últimas 24h</p>
                    <p className="text-2xl font-bold">{queueStatus.stats?.sentLastDay || 0}</p>
                    <p className="text-xs text-muted-foreground">
                      de {queueStatus.limits?.messagesPerDay || 500} permitidas
                    </p>
                  </div>
                  <div className="p-3 border rounded-lg">
                    <p className="text-sm text-muted-foreground">Total Enviado</p>
                    <p className="text-2xl font-bold text-green-600">
                      {queueStatus.stats?.totalSent || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Logs */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>📜 Últimas Mensagens Enviadas</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchLogsAndQueue}
                  disabled={loadingLogs}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loadingLogs ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingLogs ? (
                <div className="text-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin mx-auto" />
                </div>
              ) : logs.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {logs.map((log: any) => (
                    <div key={log._id} className="p-3 border rounded-lg text-sm">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <span className="font-medium">Para: {log.to}</span>
                          {log.status === 'sent' && (
                            <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded">
                              ✓ Enviado
                            </span>
                          )}
                          {log.status === 'failed' && (
                            <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded">
                              ✗ Falhou
                            </span>
                          )}
                          {log.status === 'pending' && (
                            <span className="ml-2 px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded">
                              ⏳ Pendente
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {log.deliveredAt || log.createdAt || log.attemptedAt
                            ? new Date(log.deliveredAt || log.createdAt || log.attemptedAt).toLocaleString('pt-BR')
                            : '--'}
                        </span>
                      </div>
                      <p className="text-muted-foreground line-clamp-2">{log.message}</p>
                      {log.errorMessage && (
                        <p className="text-xs text-red-600 mt-1">Erro: {log.errorMessage}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhuma mensagem enviada ainda</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default WhatsappAutomation;
